/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './ReevaluateProduct.scss';
import loading from './loading-inverse.gif';
import { StarsRating } from './StarsRating';

export function ReevaluateProduct(props) {
  const { t } = useTranslation();
  const { preview } = props;
  const { customer, token, previewId } = useParams();
  const starsRef = useRef();

  // Estados de validação
  const [isValidating, setIsValidating] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [originalReview, setOriginalReview] = useState(null);
  const [productData, setProductData] = useState(null);

  // Estados do formulário
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [recommended, setRecommended] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Estados de upload de mídia
  const [files, setFiles] = useState([]);
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    validateToken();
  }, [customer, token, previewId]);

  const validateToken = async () => {
    setIsValidating(true);
    setValidationError(null);

    try {
      let request, review, product;

      if (preview && previewId) {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/${customer}/email-preview/${previewId}`
        );
        request = response.data.request;
        review = response.data.originalReview;
        product = response.data.product;
      } else {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/${customer}/reevaluation/validate/${token}`
        );
        request = response.data.request;
        review = response.data.originalReview;
        product = response.data.product;
      }

      setRequestData(request);
      setOriginalReview(review);
      setProductData(product || null);

      // Pré-preencher com dados da avaliação anterior
      setRating(review.rating || 0);
      setText(review.text || '');
      setRecommended(review.recommended !== false);

      // Atualizar componente de estrelas
      if (starsRef.current && review.rating) {
        starsRef.current.setRating(review.rating);
      }
    } catch (err) {
      console.error('Erro ao validar token:', err);
      
      let errorMessage = t('reevaluation.error_invalid_token');
      
      if (err.response?.status === 410) {
        errorMessage = t('reevaluation.error_expired');
      } else if (err.response?.status === 409) {
        errorMessage = t('reevaluation.error_already_completed');
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setValidationError(errorMessage);
    } finally {
      setIsValidating(false);
    }
  };

  const previewFiles = (evt) => {
    const file = evt.target.files[0];

    if (file.type.includes("image")) {
      setFiles([...files, evt.target.files[0]]);
      setImages([...images, URL.createObjectURL(file)]);
      setUploadError(null);
    } else if (video == null) {
      const url = URL.createObjectURL(file);
      setFiles([...files, evt.target.files[0]]);
      setVideo(url);
      setUploadError(null);
    } else {
      setUploadError(t('errors.video_limit'));
    }

    evt.target.files = null;
    evt.target.value = "";
  };

  const removeImage = (i) => {
    const filesInst = [...files];
    const imagesInst = [...images];

    filesInst.splice(i, 1);
    imagesInst.splice(i, 1);

    setFiles(filesInst);
    setImages(imagesInst);
    setUploadError(null);
  };

  const removeVideo = () => {
    const imageFiles = files.filter((f) => f.type.includes("image"));
    setFiles(imageFiles);
    setVideo(null);
    setUploadError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (preview) {
      alert('Modo pré-visualização — nenhuma avaliação será registrada.');
      return;
    }

    if (!rating || rating === 0) {
      alert(t('reevaluation.error_no_rating'));
      return;
    }

    setIsSending(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/${customer}/${requestData.sku}/reevaluation/${token}`,
        {
          rating,
          text: text.trim(),
          recommended
        }
      );

      const { reviewId, updateToken } = response.data;
      
      console.log('Reavaliação criada:', { reviewId, updateToken });

      // Upload de imagens
      if (images.length > 0) {
        const uploadedKeys = [];
      
        for (const file of files) {
          if (file.type.includes("video"))
            continue;
            
          // 1. Solicita URL pré-assinada
          const presignResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/${customer}/${requestData.sku}/review/${reviewId}/pictures/presign`,
            {
              mimeType: file.type,
              fileSize: file.size,
            }
          );
      
          const { url, key } = presignResponse.data;
      
          // 2. Envia para o S3 diretamente
          await axios.put(url, file, {
            headers: {
              'Content-Type': file.type,
              'Content-Length': file.size,
            },
          });
      
          // 3. Armazena o key para informar ao backend depois
          uploadedKeys.push(key);
        }
      
        // 4. Confirma os arquivos enviados para o backend
        await axios.post(
          `${process.env.REACT_APP_API_URL}/${customer}/${requestData.sku}/review/${reviewId}/pictures/confirm`,
          {
            updateToken,
            keys: uploadedKeys,
          }
        );
      }

      // Upload de vídeo
      if (video != null) {
        const file = files.find((f) => f.type.includes("video"));
      
        // 1. Pede a URL pré-assinada
        const presignResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/${customer}/${requestData.sku}/review/${reviewId}/video/presign`,
          {
            mimeType: file.type,
            fileSize: file.size,
          }
        );
      
        const { url, key } = presignResponse.data;
      
        // 2. Envia o vídeo direto para o S3
        await axios.put(url, file, {
          headers: {
            "Content-Type": file.type,
            "Content-Length": file.size,
          },
        });
      
        // 3. Confirma o envio no backend
        await axios.post(
          `${process.env.REACT_APP_API_URL}/${customer}/${requestData.sku}/review/${reviewId}/video/confirm`,
          {
            updateToken: updateToken,
            key: key,
          }
        );
      }

      setSuccess(true);

      // Scroll para o topo
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Erro ao enviar reavaliação:', err);
      
      let errorMessage = t('reevaluation.error_submit');
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsSending(false);
    }
  };


  // Loading state
  if (isValidating) {
    return (
      <div className="reevaluate-product loading">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('reevaluation.validating')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (validationError) {
    return (
      <div className="reevaluate-product error">
        <div className="error-container">
          <div className="error-icon">
            <i className="bi bi-exclamation-circle"></i>
          </div>
          <h2>{t('reevaluation.error_title')}</h2>
          <p>{validationError}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="reevaluate-product success">
        <div className="success-container">
          <div className="success-icon">
            <i className="bi bi-check-circle"></i>
          </div>
          <h2>{t('reevaluation.success_title')}</h2>
          <p>{t('reevaluation.success_message')}</p>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="reevaluate-product">
      {preview && (
        <div style={{
          background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px',
          padding: '10px 16px', margin: '0 0 16px', fontFamily: 'sans-serif',
          fontSize: '13px', color: '#856404', textAlign: 'center',
        }}>
          <strong>⚠ Modo Pré-visualização</strong> — Nenhuma avaliação será registrada.
        </div>
      )}
      <div className="container">
        {/* Product Header */}
        <div className="product-header">
          {productData?.image ? (
            <img 
              src={productData.image} 
              alt={productData.name || requestData.sku}
              className="product-image"
            />
          ) : (
            <div className="product-image-placeholder">
              <i className="bi bi-box-seam"></i>
            </div>
          )}
          <div className="product-info">
            <h1>{productData?.name || requestData.sku}</h1>
            {requestData.sku && productData?.name && (
              <span className="product-sku">SKU: {requestData.sku}</span>
            )}
          </div>
        </div>

        {/* Intro */}
        <div className="reevaluation-intro">
          <h2>{t('reevaluation.intro_title')}</h2>
          <p>{t('reevaluation.intro_text')}</p>
        </div>

        {/* Reason */}
        {requestData?.reason && (
          <div className="reason-box">
            <div className="reason-icon">
              <i className="bi bi-info-circle"></i>
            </div>
            <div className="reason-content">
              <strong>{t('reevaluation.reason_label')}</strong>
              <p>{requestData.reason}</p>
            </div>
          </div>
        )}

        {/* Original Review */}
        {originalReview && (
          <div className="original-review">
            <h3>{t('reevaluation.previous_review_title')}</h3>
            <div className="review-rating">
              <div className="stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`bi bi-star${i < originalReview.rating ? '-fill' : ''}`}
                  />
                ))}
              </div>
              <span className="rating-number">
                {originalReview.rating}/5
              </span>
            </div>
            {originalReview.text && (
              <p className="review-text">"{originalReview.text}"</p>
            )}
          </div>
        )}

        {/* Reevaluation Form */}
        <form onSubmit={handleSubmit} className="reevaluation-form">
          <h3>{t('reevaluation.new_review_title')}</h3>

          {/* Rating */}
          <div className="form-group">
            <label className="form-label required">
              {t('reevaluation.rating_label')}
            </label>
            <StarsRating
              enabled={true}
              ref={starsRef}
              onChange={(value) => {
                setRating(value);
                // Auto-set recommended based on rating
                setRecommended(value >= 4);
              }}
            />
          </div>

          {/* Comment */}
          <div className="form-group">
            <label className="form-label">
              {t('reevaluation.comment_label')}
              <span className="optional"> {t('common.optional')}</span>
            </label>
            <textarea
              className="form-control"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              placeholder={t('reevaluation.comment_placeholder')}
            />
          </div>

          {/* Recommended */}
          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={recommended}
                onChange={(e) => setRecommended(e.target.checked)}
              />
              <span>{t('reevaluation.recommend_label')}</span>
            </label>
          </div>

          {/* Upload de Fotos/Vídeo */}
          <div className="form-group">
            <label className="form-label">
              {t('upload.both')}
              <span className="optional"> {t('common.optional')}</span>
            </label>
            <span className="description">
              {t('upload_tip')}
            </span>

            <ul className="pictures">
              {images && images.length > 0 && (
                <>
                  {images.map((image, i) => {
                    return (
                      <li key={i}>
                        <img alt={t('alt.photo', { number: i + 1 })} src={image} />
                        <span
                          className="close"
                          onClick={() => {
                            removeImage(i);
                          }}
                        >
                          X
                        </span>
                      </li>
                    );
                  })}
                </>
              )}
              {video != null && (
                <li>
                  <video src={video} height="60" width="60" />
                  <span
                    className="close"
                    onClick={() => {
                      removeVideo();
                    }}
                  >
                    X
                  </span>
                </li>
              )}
              <li className="add">
                <button
                  className="add-files"
                  onClick={(evt) => {
                    evt.preventDefault();
                    document.getElementById("files").click();
                  }}
                />
              </li>
            </ul>

            {uploadError && (
              <div className="error">{uploadError}</div>
            )}

            <input
              type="file"
              className="file-upload"
              id="files"
              accept="image/jpeg,image/png,image/webp,video/mpeg,video/ogg,video/webm,video/mp4,video/quicktime"
              onChange={previewFiles}
            />
          </div>

          {/* Submit */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn-submit"
              disabled={!rating || isSending}
            >
              {isSending ? (
                <>
                  <img src={loading} alt={t('common.sending')} />
                  {t('common.sending')}
                </>
              ) : (
                <>
                  <i className="bi bi-send"></i>
                  {t('reevaluation.submit_button')}
                </>
              )}
            </button>
          </div>
        </form>

        {/* Expiration Notice */}
        {requestData?.expiresAt && (
          <div className="expiration-notice">
            <i className="bi bi-clock"></i>
            {t('reevaluation.expires_at', {
              date: new Date(requestData.expiresAt).toLocaleDateString()
            })}
          </div>
        )}
      </div>
    </div>
  );
}

